/** XeAgents.ai - the Apollo.io tab of AI B2B Lead: chat-based lead search. **/
import { useState, useRef, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { useTranslation } from "react-i18next";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { toIsoString, formatDate } from "@/lib/date-format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { flatTimezoneList, getCurrentTimeInTimezone } from "@/lib/country-timezones";
import {
  Loader2, CheckCircle2, Target, Trash2, Clock, Globe, Calendar,
  AlertCircle, Search, Sparkles, Users, ArrowLeft, ArrowRight,
  Phone, Building, Briefcase, Mail, Plus, Send, Bot, User,
  MapPin, TrendingUp, Filter, X, ChevronDown, ChevronUp,
  TableIcon, Save, Check, Eye, Linkedin, ExternalLink,
  History, MessageSquare, MoreVertical, Download
} from "lucide-react";

interface Agent {
  id: string;
  name: string;
  personality: string;
  type: 'incoming' | 'flow';
  isActive?: boolean;
  voiceProvider?: string;
  flowId?: string;
}

interface ApolloLead {
  id?: string;
  firstName: string;
  lastName: string;
  phone: string;
  email: string | null;
  company: string | null;
  title: string | null;
  linkedinUrl?: string | null;
  // Extended fields
  industry?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  website?: string | null;
  employeeCount?: number | null;
  seniority?: string | null;
  departments?: string[];
  companyLinkedIn?: string | null;
}

interface ExistingCampaign {
  id: string;
  name: string;
  type?: string;
  agentId?: string;
  contactCount?: number;
  status?: string;
}

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  searchParams?: SearchParameters;
  results?: {
    totalFound: number;
    hasPhoneNumbers: number;
    leadsWithPhones: ApolloLead[];
  };
}

interface SearchParameters {
  person_titles?: string[];
  person_seniorities?: string[];
  person_locations?: string[];
  organization_industries?: string[];
  organization_locations?: string[];
  organization_employee_ranges?: string[];
  maxResults?: number;
}

// ChatGPT-style session history
interface SearchSession {
  id: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  chatMessages: ChatMessage[];
  selectedLeads: ApolloLead[];
  allSearchResults: ApolloLead[];
  currentParams: SearchParameters;
  showResultsTable: boolean;
}

const SESSIONS_STORAGE_KEY = 'smart-prospecting-sessions';
const CURRENT_SESSION_KEY = 'smart-prospecting-current-session';

const CAMPAIGN_TYPES = [
  "Lead Qualification",
  "Feedback Collection",
  "Promotional",
  "Payment Reminder",
  "Event Promotion",
  "Appointment Setting",
  "Customer Survey",
  "Product Announcement",
  "Follow-up",
  "Other",
];

const WEEKDAYS = [
  { id: "monday", label: "Mon" },
  { id: "tuesday", label: "Tue" },
  { id: "wednesday", label: "Wed" },
  { id: "thursday", label: "Thu" },
  { id: "friday", label: "Fri" },
  { id: "saturday", label: "Sat" },
  { id: "sunday", label: "Sun" },
];

// Quick filter suggestions
const QUICK_FILTERS = [
  { label: "C-Suite Only", query: "only show c-suite executives" },
  { label: "Add Tech Industry", query: "also include technology companies" },
  { label: "Add Healthcare", query: "also add healthcare industry" },
  { label: "US Only", query: "only people in United States" },
  { label: "Small Companies", query: "companies with 10-50 employees" },
  { label: "Enterprise", query: "large enterprises with 1000+ employees" },
  { label: "More Results", query: "get 50 more leads" },
  { label: "Sales Roles", query: "add sales directors and VP sales" },
];

const SENIORITY_OPTIONS = [
  { value: "c_suite", label: "C-Suite" },
  { value: "founder", label: "Founder" },
  { value: "owner", label: "Owner" },
  { value: "vp", label: "VP" },
  { value: "director", label: "Director" },
  { value: "head", label: "Head" },
  { value: "manager", label: "Manager" },
  { value: "senior", label: "Senior" },
];

const EMPLOYEE_RANGES = [
  { value: "1,10", label: "1-10" },
  { value: "11,50", label: "11-50" },
  { value: "51,200", label: "51-200" },
  { value: "201,500", label: "201-500" },
  { value: "501,1000", label: "501-1K" },
  { value: "1001,5000", label: "1K-5K" },
  { value: "5001,10000", label: "5K-10K" },
  { value: "10001+", label: "10K+" },
];

export function ApolloProspectSearch({ embedded = false }: { embedded?: boolean } = {}) {
  const [, setLocation] = useLocation();
  const { t } = useTranslation();
  const { toast } = useToast();
  const chatEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Tab state (search-results is the default/first tab)
  const [activeTab, setActiveTab] = useState("search-results");

  // Campaign details state
  const [name, setName] = useState("");
  const [type, setType] = useState("Lead Qualification");
  const [goal, setGoal] = useState("");
  const [agentId, setAgentId] = useState("");

  // Schedule state
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduleTimeStart, setScheduleTimeStart] = useState("09:00");
  const [scheduleTimeEnd, setScheduleTimeEnd] = useState("17:00");
  const [scheduleDays, setScheduleDays] = useState<string[]>(["monday", "tuesday", "wednesday", "thursday", "friday"]);
  const [scheduleTimezone, setScheduleTimezone] = useState(() => {
    // Auto-detect user's timezone from browser
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York";
    } catch {
      return "America/New_York";
    }
  });

  // Default welcome message
  const getWelcomeMessage = (): ChatMessage => ({
    id: '1',
    role: 'assistant',
    content: "Hi! I'm your AI lead assistant. Tell me what kind of leads you're looking for and I'll find them for you.\n\nYou can say things like:\n• \"Find 50 CTOs at tech startups in California\"\n• \"Get marketing directors at healthcare companies\"\n• \"Search for sales VPs in New York with 100-500 employees\"\n\nI'll remember our conversation, so you can refine your search by saying things like \"add more from finance\" or \"only show C-suite\".",
    timestamp: new Date(),
  });

  // Session management functions
  const loadAllSessions = (): SearchSession[] => {
    try {
      const saved = localStorage.getItem(SESSIONS_STORAGE_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  };

  const loadCurrentSessionId = (): string | null => {
    try {
      return localStorage.getItem(CURRENT_SESSION_KEY);
    } catch {
      return null;
    }
  };

  const generateSessionTitle = (messages: ChatMessage[]): string => {
    const userMessage = messages.find(m => m.role === 'user');
    if (userMessage) {
      const content = userMessage.content.trim();
      return content.length > 40 ? content.substring(0, 40) + '...' : content;
    }
    return 'New Search';
  };

  // Initialize sessions state
  const [sessions, setSessions] = useState<SearchSession[]>(loadAllSessions);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(loadCurrentSessionId);
  const [showSessionHistory, setShowSessionHistory] = useState(() => typeof window === "undefined" || window.innerWidth >= 1280); // Default: visible on wide screens, collapsed on laptops/tablets

  // Get current session data
  const getCurrentSession = (): SearchSession | null => {
    if (!currentSessionId) return null;
    return sessions.find(s => s.id === currentSessionId) || null;
  };

  const currentSession = getCurrentSession();

  // Chat-based Apollo AI Search state - initialize from current session or defaults
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>(() => {
    if (currentSession?.chatMessages) {
      return currentSession.chatMessages.map(msg => ({
        ...msg,
        timestamp: new Date(msg.timestamp)
      }));
    }
    return [getWelcomeMessage()];
  });
  const [chatInput, setChatInput] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [currentParams, setCurrentParams] = useState<SearchParameters>(currentSession?.currentParams || {});
  const [selectedLeads, setSelectedLeads] = useState<ApolloLead[]>(currentSession?.selectedLeads || []);
  const [showFilters, setShowFilters] = useState(false);

  // All search results (before selection)
  const [allSearchResults, setAllSearchResults] = useState<ApolloLead[]>(currentSession?.allSearchResults || []);
  const [showResultsTable, setShowResultsTable] = useState(currentSession?.showResultsTable || false);

  // Result selection state (for checkbox selection in table)
  const [selectedResultIds, setSelectedResultIds] = useState<Set<string>>(new Set());

  // Campaign selection modal state
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [selectedExistingCampaign, setSelectedExistingCampaign] = useState<string>("");
  const [createNewCampaign, setCreateNewCampaign] = useState(true);

  // Duplicate detection state
  const [duplicateLeads, setDuplicateLeads] = useState<ApolloLead[]>([]);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicateAction, setDuplicateAction] = useState<'skip' | 'merge' | 'overwrite'>('skip');

  // Pagination state for results
  const [resultsPage, setResultsPage] = useState(1);
  const resultsPerPage = 50;

  // Search history
  const [searchHistory, setSearchHistory] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem('apollo-search-history');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  // Submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSavingToCampaign, setIsSavingToCampaign] = useState(false);

  // Phone enrichment polling state
  const [isEnrichingPhones, setIsEnrichingPhones] = useState(false);
  const [phoneRequestIds, setPhoneRequestIds] = useState<string[]>([]);
  const phonePollingRef = useRef<NodeJS.Timeout | null>(null);
  // Store pending leads during enrichment - don't show table until done
  const pendingLeadsRef = useRef<ApolloLead[]>([]);

  // Save search to history
  const addToSearchHistory = (query: string) => {
    const trimmed = query.trim();
    if (!trimmed) return;
    setSearchHistory(prev => {
      const filtered = prev.filter(q => q !== trimmed);
      const updated = [trimmed, ...filtered].slice(0, 10); // Keep last 10 searches
      localStorage.setItem('apollo-search-history', JSON.stringify(updated));
      return updated;
    });
  };

  // Save current session to localStorage
  const saveCurrentSession = () => {
    if (!currentSessionId) return;

    const updatedSession: SearchSession = {
      id: currentSessionId,
      title: generateSessionTitle(chatMessages),
      createdAt: currentSession?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      chatMessages,
      selectedLeads,
      allSearchResults,
      currentParams,
      showResultsTable,
    };

    setSessions(prev => {
      const filtered = prev.filter(s => s.id !== currentSessionId);
      const updated = [updatedSession, ...filtered].slice(0, 20); // Keep last 20 sessions
      localStorage.setItem(SESSIONS_STORAGE_KEY, JSON.stringify(updated));
      return updated;
    });
  };

  // Create a new session
  const createNewSession = () => {
    const newId = `session-${Date.now()}`;
    const newSession: SearchSession = {
      id: newId,
      title: 'New Search',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      chatMessages: [getWelcomeMessage()],
      selectedLeads: [],
      allSearchResults: [],
      currentParams: {},
      showResultsTable: false,
    };

    setSessions(prev => {
      const updated = [newSession, ...prev].slice(0, 20);
      localStorage.setItem(SESSIONS_STORAGE_KEY, JSON.stringify(updated));
      return updated;
    });

    setCurrentSessionId(newId);
    localStorage.setItem(CURRENT_SESSION_KEY, newId);

    // Reset state
    setChatMessages([getWelcomeMessage()]);
    setSelectedLeads([]);
    setAllSearchResults([]);
    setCurrentParams({});
    setShowResultsTable(false);
    setSelectedResultIds(new Set());

    toast({
      title: "New search started",
      description: "Your previous session has been saved.",
    });
  };

  // Load a session
  const loadSession = (sessionId: string) => {
    const session = sessions.find(s => s.id === sessionId);
    if (!session) return;

    // Save current session first
    if (currentSessionId && chatMessages.length > 1) {
      saveCurrentSession();
    }

    setCurrentSessionId(sessionId);
    localStorage.setItem(CURRENT_SESSION_KEY, sessionId);

    setChatMessages(session.chatMessages.map(msg => ({
      ...msg,
      timestamp: new Date(msg.timestamp)
    })));
    setSelectedLeads(session.selectedLeads);
    setAllSearchResults(session.allSearchResults);
    setCurrentParams(session.currentParams);
    setShowResultsTable(session.showResultsTable);
    setSelectedResultIds(new Set());
    setShowSessionHistory(false);

    toast({
      title: "Session loaded",
      description: session.title,
    });
  };

  // Delete a session
  const deleteSession = (sessionId: string, e?: React.MouseEvent) => {
    e?.stopPropagation();

    setSessions(prev => {
      const updated = prev.filter(s => s.id !== sessionId);
      localStorage.setItem(SESSIONS_STORAGE_KEY, JSON.stringify(updated));
      return updated;
    });

    // If deleting current session, create a new one
    if (sessionId === currentSessionId) {
      createNewSession();
    }

    toast({
      title: "Session deleted",
    });
  };

  // Clear/start fresh (saves current and creates new)
  const clearSession = () => {
    // Save current session if it has content
    if (currentSessionId && chatMessages.length > 1) {
      saveCurrentSession();
    }
    createNewSession();
  };

  // Auto-save current session when state changes
  useEffect(() => {
    if (currentSessionId && chatMessages.length > 1) {
      const timeoutId = setTimeout(() => {
        saveCurrentSession();
      }, 1000); // Debounce saves
      return () => clearTimeout(timeoutId);
    }
  }, [chatMessages, selectedLeads, allSearchResults, currentParams, showResultsTable]);

  // Initialize a session on first load if none exists
  useEffect(() => {
    if (!currentSessionId && sessions.length === 0) {
      const newId = `session-${Date.now()}`;
      setCurrentSessionId(newId);
      localStorage.setItem(CURRENT_SESSION_KEY, newId);
    } else if (!currentSessionId && sessions.length > 0) {
      // Load the most recent session
      loadSession(sessions[0].id);
    }
  }, []);

  // Poll for phone enrichment results - updates pending leads and shows when done
  const pollPhoneEnrichment = async (requestIds: string[], retryCount = 0) => {
    if (requestIds.length === 0) {
      // No request IDs - just show existing pending leads
      if (pendingLeadsRef.current.length > 0) {
        console.log(`[Phone Poll] No request IDs, showing ${pendingLeadsRef.current.length} leads without enrichment`);
        setAllSearchResults(pendingLeadsRef.current);
        setShowResultsTable(true);
        pendingLeadsRef.current = [];
      }
      setIsEnrichingPhones(false);
      return;
    }

    try {
      const response = await apiRequest("POST", "/api/apollo/poll-phones", { requestIds });
      const result = await response.json();

      console.log(`[Phone Poll] Result: phones=${result.totalPhones}, pending=${result.pending}, completed=${result.completed}`);

      // Update pending leads with phone numbers from this poll
      if (result.success && result.totalPhones > 0) {
        console.log(`[Phone Poll] Got ${result.totalPhones} phones, updating pending leads...`);

        // Update pending leads ref with phone numbers
        const allPhones = Object.values(result.phoneResults).flat() as any[];
        let phoneIndex = 0;
        pendingLeadsRef.current = pendingLeadsRef.current.map(lead => {
          if (!lead.phone && phoneIndex < allPhones.length) {
            const phoneData = allPhones[phoneIndex++];
            if (phoneData) {
              return {
                ...lead,
                phone: phoneData.sanitized_number || phoneData.raw_number || '',
              };
            }
          }
          return lead;
        });
      }

      // If still pending and not too many retries, poll again
      if (result.pending > 0 && retryCount < 5) {
        console.log(`[Phone Poll] ${result.pending} still pending, retrying in 5s (attempt ${retryCount + 1}/5)...`);
        phonePollingRef.current = setTimeout(() => {
          pollPhoneEnrichment(requestIds, retryCount + 1);
        }, 5000);
      } else {
        // Done polling - NOW show all results together
        console.log(`[Phone Poll] Done polling. Showing ${pendingLeadsRef.current.length} leads with enriched data.`);
        setAllSearchResults(pendingLeadsRef.current);
        setShowResultsTable(true);
        pendingLeadsRef.current = [];
        setIsEnrichingPhones(false);
        setPhoneRequestIds([]);
      }
    } catch (error) {
      console.error('[Phone Poll] Error:', error);
      // On error, show what we have
      if (pendingLeadsRef.current.length > 0) {
        setAllSearchResults(pendingLeadsRef.current);
        setShowResultsTable(true);
        pendingLeadsRef.current = [];
      }
      setIsEnrichingPhones(false);
      setPhoneRequestIds([]);
    }
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (phonePollingRef.current) {
        clearTimeout(phonePollingRef.current);
      }
    };
  }, []);

  // Auto-scroll chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  // Fetch agents (only flow agents for campaigns)
  const { data: agents = [] } = useQuery<Agent[]>({
    queryKey: ["/api/agents"],
    select: (data) => data.filter(a => a.type === 'flow' && a.isActive !== false),
  });

  // Fetch existing campaigns for "Save to Campaign" modal
  const { data: existingCampaigns = [] } = useQuery<ExistingCampaign[]>({
    queryKey: ["/api/campaigns"],
    select: (data: any[]) => data.map(c => ({
      id: c.id,
      name: c.name,
      type: c.type,
      agentId: c.agentId,
      contactCount: c.contactCount || 0,
      status: c.status,
    })),
  });

  // Send chat message and search
  const handleSendMessage = async (messageText?: string) => {
    const query = messageText || chatInput.trim();
    if (!query) return;

    // Add to search history
    addToSearchHistory(query);

    // Add user message
    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: query,
      timestamp: new Date(),
    };
    setChatMessages(prev => [...prev, userMessage]);
    setChatInput("");
    setIsSearching(true);
    setResultsPage(1); // Reset pagination on new search

    try {
      // Build context from previous messages for conversational search
      const conversationHistory = chatMessages
        .filter(m => m.role === 'user')
        .map(m => m.content)
        .join('\n');

      const response = await apiRequest("POST", "/api/apollo/ai-search-chat", {
        query,
        conversationHistory,
        currentParams,
        maxResults: 100,
      });

      const result = await response.json();

      if (result.success) {
        // Update current params
        setCurrentParams(result.interpretedParams || {});

        // Extract ALL leads (with unique ID for tracking)
        const allLeads = result.people.map((p: any, index: number) => ({
          id: p.id || `lead-${Date.now()}-${index}`,
          firstName: p.first_name || 'Unknown',
          lastName: p.last_name || '',
          phone: p.phone_numbers?.[0]?.sanitized_number || p.phone_numbers?.[0]?.raw_number || '',
          email: p.email || null,
          company: p.organization_name || p.organization?.name || null,
          title: p.title || null,
          linkedinUrl: p.linkedin_url || null,
          // Extended fields
          industry: p.organization?.industry || null,
          city: p.city || p.organization?.city || null,
          state: p.state || p.organization?.state || null,
          country: p.country || p.organization?.country || null,
          website: p.organization?.website_url || null,
          employeeCount: p.organization?.estimated_num_employees || null,
          seniority: p.seniority || null,
          departments: p.departments || [],
          companyLinkedIn: p.organization?.linkedin_url || null,
        }));

        // Count leads with phone numbers
        const leadsWithPhones = allLeads.filter((l: ApolloLead) => l.phone);

        // Check if we need to wait for phone enrichment
        const needsEnrichment = leadsWithPhones.length === 0 && allLeads.length > 0;
        const hasRequestIds = result.phoneRequestIds?.length > 0;

        if (needsEnrichment || (result.phoneEnrichmentPending && hasRequestIds)) {
          // Store leads in ref - don't show table yet
          console.log(`[Search] Storing ${allLeads.length} leads for enrichment. RequestIds: ${result.phoneRequestIds?.length || 0}`);
          pendingLeadsRef.current = allLeads;
          setIsEnrichingPhones(true);
          // Don't show table yet - it will be shown when polling completes

          if (hasRequestIds) {
            setPhoneRequestIds(result.phoneRequestIds);
            // Start polling after 8 seconds (Apollo needs time to process)
            setTimeout(() => {
              pollPhoneEnrichment(result.phoneRequestIds);
            }, 8000);
          } else {
            // No request IDs - try direct enrichment as fallback
            console.log(`[Search] No request IDs, trying direct enrichment...`);
            const personIds = allLeads.map((l: ApolloLead) => l.id).filter(Boolean) as string[];

            setTimeout(async () => {
              try {
                if (personIds.length > 0) {
                  const response = await apiRequest("POST", "/api/apollo/poll-phones", { requestIds: [], personIds });
                  const pollResult = await response.json();
                  console.log(`[Search] Fallback enrichment result:`, pollResult);

                  // Update pending leads with any phones found
                  if (pollResult.totalPhones > 0) {
                    const allPhones = Object.values(pollResult.phoneResults).flat() as any[];
                    let phoneIndex = 0;
                    pendingLeadsRef.current = pendingLeadsRef.current.map(lead => {
                      if (!lead.phone && phoneIndex < allPhones.length) {
                        const phoneData = allPhones[phoneIndex++];
                        if (phoneData) {
                          return {
                            ...lead,
                            phone: phoneData.sanitized_number || phoneData.raw_number || '',
                          };
                        }
                      }
                      return lead;
                    });
                  }
                }
              } catch (err) {
                console.error('[Search] Fallback enrichment failed:', err);
              } finally {
                // Show results after enrichment attempt
                console.log(`[Search] Showing ${pendingLeadsRef.current.length} leads after fallback enrichment`);
                setAllSearchResults(pendingLeadsRef.current);
                setShowResultsTable(true);
                pendingLeadsRef.current = [];
                setIsEnrichingPhones(false);
              }
            }, 5000);
          }
        } else {
          // Already have phones - show immediately
          console.log(`[Search] ${leadsWithPhones.length}/${allLeads.length} leads have phones, showing immediately`);
          setAllSearchResults(allLeads);
          setShowResultsTable(true);
        }

        // Build assistant response
        let responseText = result.explanation || "Here's what I found:";
        if (result.phoneEnrichmentPending) {
          responseText += `\n\n📊 Found ${result.totalFound} leads. Enriching phone numbers...`;
        } else {
          responseText += `\n\n📊 Found ${result.totalFound} leads, ${leadsWithPhones.length} with phone numbers.`;
        }
        responseText += `\n\n👆 View the results table below and click "Save" to add leads to your campaign.`;

        if (result.warnings?.length > 0) {
          responseText += `\n\n⚠️ ${result.warnings.join('\n')}`;
        }

        responseText += "\n\nWant to refine? Try: \"only C-suite\", \"add tech industry\", or \"get 50 more\"";

        const assistantMessage: ChatMessage = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: responseText,
          timestamp: new Date(),
          searchParams: result.interpretedParams,
          results: {
            totalFound: result.totalFound,
            hasPhoneNumbers: leadsWithPhones.length,
            leadsWithPhones,
          },
        };
        setChatMessages(prev => [...prev, assistantMessage]);

      } else {
        const errorMessage: ChatMessage = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: `❌ ${result.warnings?.[0] || "Search failed. Please try again."}`,
          timestamp: new Date(),
        };
        setChatMessages(prev => [...prev, errorMessage]);
      }
    } catch (error: any) {
      const errorMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: `❌ Error: ${error.message || "Failed to search for leads"}`,
        timestamp: new Date(),
      };
      setChatMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsSearching(false);
      inputRef.current?.focus();
    }
  };

  // Handle quick filter click
  const handleQuickFilter = (filter: { label: string; query: string }) => {
    handleSendMessage(filter.query);
  };

  // Handle keyboard submit
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Remove a lead from selection
  const removeLead = (index: number) => {
    setSelectedLeads(prev => prev.filter((_, i) => i !== index));
  };

  // Clear all leads
  const clearAllLeads = () => {
    setSelectedLeads([]);
    toast({ title: "Cleared all leads" });
  };

  // Check if a lead is already saved
  const isLeadSaved = (lead: ApolloLead) => {
    return selectedLeads.some(l =>
      (l.phone && l.phone === lead.phone) ||
      (l.email && l.email === lead.email) ||
      (l.id && l.id === lead.id)
    );
  };

  // Save a single lead
  const saveLead = (lead: ApolloLead) => {
    if (!lead.phone) {
      toast({ title: "Cannot save lead without phone number", variant: "destructive" });
      return;
    }
    if (isLeadSaved(lead)) {
      toast({ title: "Lead already saved" });
      return;
    }
    setSelectedLeads(prev => [...prev, lead]);
    toast({ title: `Saved ${lead.firstName} ${lead.lastName}` });
  };

  // Save all leads with phone numbers
  const saveAllLeadsWithPhone = () => {
    const leadsWithPhone = allSearchResults.filter(l => l.phone && !isLeadSaved(l));
    if (leadsWithPhone.length === 0) {
      toast({ title: "No new leads to save" });
      return;
    }
    setSelectedLeads(prev => [...prev, ...leadsWithPhone]);
    toast({ title: `Saved ${leadsWithPhone.length} leads` });
  };

  // Toggle result selection in the table
  const toggleResultSelection = (leadId: string) => {
    console.log('[CampaignAI] toggleResultSelection called with leadId:', leadId);
    setSelectedResultIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(leadId)) {
        newSet.delete(leadId);
        console.log('[CampaignAI] Removed lead from selection. New size:', newSet.size);
      } else {
        newSet.add(leadId);
        console.log('[CampaignAI] Added lead to selection. New size:', newSet.size);
      }
      return newSet;
    });
  };

  const saveSelectedResults = () => {
    const selectedLeadsList = allSearchResults.filter(l => selectedResultIds.has(l.id || ''));
    const leadsToSave = selectedLeadsList.filter(l => l.phone && !isLeadSaved(l));
    const leadsWithoutPhone = selectedLeadsList.filter(l => !l.phone);

    if (leadsToSave.length === 0) {
      if (leadsWithoutPhone.length > 0) {
        toast({
          title: "Cannot save leads without phone numbers",
          description: `${leadsWithoutPhone.length} selected leads need phone enrichment first.`
        });
      } else {
        toast({ title: "No valid leads selected" });
      }
      return;
    }
    setSelectedLeads(prev => [...prev, ...leadsToSave]);
    setSelectedResultIds(new Set());
    const skippedMsg = leadsWithoutPhone.length > 0 ? ` (${leadsWithoutPhone.length} skipped - no phone)` : '';
    toast({ title: `Saved ${leadsToSave.length} leads${skippedMsg}` });
  };

  const selectAllResults = () => {
    const allIds = allSearchResults.filter(l => !isLeadSaved(l)).map(l => l.id || '');
    setSelectedResultIds(new Set(allIds));
  };

  const deselectAllResults = () => {
    setSelectedResultIds(new Set());
  };

  // Download search results as CSV
  const downloadSearchResultsCSV = () => {
    if (allSearchResults.length === 0) {
      toast({ title: "No results to download", variant: "destructive" });
      return;
    }

    // CSV headers
    const headers = [
      "First Name",
      "Last Name",
      "Phone",
      "Email",
      "Company",
      "Job Title",
      "Industry",
      "City",
      "State",
      "Country",
      "Website",
      "Employee Count",
      "Seniority",
      "LinkedIn URL",
      "Company LinkedIn"
    ];

    // Convert leads to CSV rows
    const rows = allSearchResults.map(lead => [
      lead.firstName || "",
      lead.lastName || "",
      lead.phone || "",
      lead.email || "",
      lead.company || "",
      lead.title || "",
      lead.industry || "",
      lead.city || "",
      lead.state || "",
      lead.country || "",
      lead.website || "",
      lead.employeeCount?.toString() || "",
      lead.seniority || "",
      lead.linkedinUrl || "",
      lead.companyLinkedIn || ""
    ]);

    // Escape CSV values (handle commas, quotes, newlines)
    const escapeCSV = (value: string) => {
      if (value.includes(",") || value.includes('"') || value.includes("\n")) {
        return `"${value.replace(/"/g, '""')}"`;
      }
      return value;
    };

    // Build CSV content
    const csvContent = [
      headers.map(escapeCSV).join(","),
      ...rows.map(row => row.map(escapeCSV).join(","))
    ].join("\n");

    // Create and download file
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `prospect-ai-results-${new Date().toISOString().split("T")[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast({
      title: "CSV Downloaded",
      description: `${allSearchResults.length} leads exported successfully`,
    });
  };

  // Download CSV from a specific session
  const downloadSessionCSV = (session: SearchSession, e?: React.MouseEvent) => {
    e?.stopPropagation();

    const leads = session.allSearchResults;
    if (leads.length === 0) {
      toast({ title: "No results in this session", variant: "destructive" });
      return;
    }

    const headers = [
      "First Name", "Last Name", "Phone", "Email", "Company", "Job Title",
      "Industry", "City", "State", "Country", "Website", "Employee Count",
      "Seniority", "LinkedIn URL", "Company LinkedIn"
    ];

    const rows = leads.map(lead => [
      lead.firstName || "", lead.lastName || "", lead.phone || "", lead.email || "",
      lead.company || "", lead.title || "", lead.industry || "", lead.city || "",
      lead.state || "", lead.country || "", lead.website || "",
      lead.employeeCount?.toString() || "", lead.seniority || "",
      lead.linkedinUrl || "", lead.companyLinkedIn || ""
    ]);

    const escapeCSV = (value: string) => {
      if (value.includes(",") || value.includes('"') || value.includes("\n")) {
        return `"${value.replace(/"/g, '""')}"`;
      }
      return value;
    };

    const csvContent = [
      headers.map(escapeCSV).join(","),
      ...rows.map(row => row.map(escapeCSV).join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `prospect-ai-${session.title.slice(0, 30).replace(/[^a-zA-Z0-9]/g, '-')}-${toIsoString(session.createdAt, new Date().toISOString()).split("T")[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast({
      title: "CSV Downloaded",
      description: `${leads.length} leads from "${session.title}" exported`,
    });
  };

  // Toggle day selection
  const toggleDay = (day: string) => {
    setScheduleDays(prev =>
      prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]
    );
  };

  // Create campaign mutation
  const createCampaignMutation = useMutation({
    mutationFn: async () => {
      const campaignRes = await apiRequest("POST", "/api/campaigns", {
        name,
        type,
        goal,
        agentId,
        scheduleEnabled,
        scheduleTimeStart: scheduleEnabled ? scheduleTimeStart : null,
        scheduleTimeEnd: scheduleEnabled ? scheduleTimeEnd : null,
        scheduleDays: scheduleEnabled ? scheduleDays : null,
        scheduleTimezone: scheduleTimezone, // Always save timezone for follow-up calls
      });

      const campaign = await campaignRes.json();

      if (selectedLeads.length > 0) {
        await apiRequest("POST", `/api/campaigns/${campaign.id}/contacts/import-json`, {
          contacts: selectedLeads.map(lead => ({
            firstName: lead.firstName,
            lastName: lead.lastName,
            phone: lead.phone,
            email: lead.email,
            // Include all voice agent compatibility fields
            customFields: {
              company: lead.company,
              jobTitle: lead.title,
              industry: lead.industry,
              city: lead.city,
              state: lead.state,
              country: lead.country,
              website: lead.website,
              employeeCount: lead.employeeCount,
              seniority: lead.seniority,
              linkedinUrl: lead.linkedinUrl,
              companyLinkedIn: lead.companyLinkedIn,
              source: 'apollo.io',
              // Voice agent compatibility defaults
              status: 'ready',
              language: 'en',
            },
          })),
        });
      }

      return campaign;
    },
    onSuccess: (campaign) => {
      queryClient.invalidateQueries({ queryKey: ["/api/campaigns"] });
      const campaignId = campaign.id;
      const campaignName = name;
      const leadsCount = selectedLeads.length;

      // Save current session and reset for new searches (do this first before showing toast)
      saveCurrentSession();
      const newId = `session-${Date.now()}`;
      setCurrentSessionId(newId);
      localStorage.setItem(CURRENT_SESSION_KEY, newId);
      setChatMessages([getWelcomeMessage()]);
      setSelectedLeads([]);
      setAllSearchResults([]);
      setCurrentParams({});
      setShowResultsTable(false);
      setSelectedResultIds(new Set());

      // Reset campaign form fields
      setName("");
      setType("Lead Qualification");
      setGoal("");
      setAgentId("");
      setIsSubmitting(false);

      // Switch back to search tab
      setActiveTab("search-results");

      // Show toast with action to view campaign
      toast({
        title: "Campaign Created",
        description: `${campaignName} created with ${leadsCount} leads. Search saved in History.`,
        action: (
          <ToastAction altText="View Campaign" onClick={() => setLocation(`/app/campaigns/${campaignId}`)}>
            View
          </ToastAction>
        ),
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to create campaign",
        description: error.message || "Please try again",
        variant: "destructive",
      });
    },
  });

  // Save leads to existing campaign mutation
  const saveToExistingCampaignMutation = useMutation({
    mutationFn: async (campaignId: string) => {
      const response = await apiRequest("POST", `/api/campaigns/${campaignId}/contacts/import-json`, {
        contacts: selectedLeads.map(lead => ({
          firstName: lead.firstName,
          lastName: lead.lastName,
          phone: lead.phone,
          email: lead.email,
          customFields: {
            company: lead.company,
            jobTitle: lead.title,
            industry: lead.industry,
            city: lead.city,
            state: lead.state,
            country: lead.country,
            website: lead.website,
            employeeCount: lead.employeeCount,
            seniority: lead.seniority,
            linkedinUrl: lead.linkedinUrl,
            source: 'apollo.io',
          },
        })),
      });
      return response.json();
    },
    onSuccess: (data, campaignId) => {
      queryClient.invalidateQueries({ queryKey: ["/api/campaigns"] });
      const leadsCount = selectedLeads.length;
      setShowCampaignModal(false);

      // Save current session and reset for new searches
      saveCurrentSession();
      const newId = `session-${Date.now()}`;
      setCurrentSessionId(newId);
      localStorage.setItem(CURRENT_SESSION_KEY, newId);
      setChatMessages([getWelcomeMessage()]);
      setSelectedLeads([]);
      setAllSearchResults([]);
      setCurrentParams({});
      setShowResultsTable(false);
      setSelectedResultIds(new Set());
      setSelectedExistingCampaign("");

      // Show toast with action to view campaign
      toast({
        title: "Leads Saved",
        description: `${leadsCount} leads added to campaign. Search saved in History.`,
        action: (
          <ToastAction altText="View Campaign" onClick={() => setLocation(`/app/campaigns/${campaignId}`)}>
            View
          </ToastAction>
        ),
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to save leads",
        description: error.message || "Please try again",
        variant: "destructive",
      });
    },
  });

  // Check for duplicates before saving
  const checkDuplicates = async (leads: ApolloLead[], campaignId: string): Promise<ApolloLead[]> => {
    try {
      const response = await apiRequest("POST", `/api/campaigns/${campaignId}/contacts/check-duplicates`, {
        phones: leads.map(l => l.phone).filter(Boolean),
        emails: leads.map(l => l.email).filter(Boolean),
      });
      const result = await response.json();
      return leads.filter(lead =>
        result.duplicatePhones?.includes(lead.phone) ||
        result.duplicateEmails?.includes(lead.email)
      );
    } catch {
      // If endpoint doesn't exist, proceed without duplicate check
      return [];
    }
  };

  // Handle save to campaign with duplicate detection
  const handleSaveToCampaign = async () => {
    if (selectedLeads.length === 0) {
      toast({ title: "No leads selected", variant: "destructive" });
      return;
    }

    if (createNewCampaign) {
      // Open the step 1 to create new campaign
      setShowCampaignModal(false);
      setActiveTab("campaign-settings");
      return;
    }

    if (!selectedExistingCampaign) {
      toast({ title: "Please select a campaign", variant: "destructive" });
      return;
    }

    setIsSavingToCampaign(true);

    // Check for duplicates
    const duplicates = await checkDuplicates(selectedLeads, selectedExistingCampaign);

    if (duplicates.length > 0) {
      setDuplicateLeads(duplicates);
      setShowDuplicateModal(true);
      setIsSavingToCampaign(false);
      return;
    }

    // No duplicates, save directly
    saveToExistingCampaignMutation.mutate(selectedExistingCampaign);
    setIsSavingToCampaign(false);
  };

  // Handle duplicate resolution
  const handleDuplicateResolution = () => {
    setShowDuplicateModal(false);

    if (duplicateAction === 'skip') {
      // Remove duplicates from selection
      const duplicatePhones = new Set(duplicateLeads.map(l => l.phone));
      const duplicateEmails = new Set(duplicateLeads.map(l => l.email));
      const filteredLeads = selectedLeads.filter(l =>
        !duplicatePhones.has(l.phone) && !duplicateEmails.has(l.email)
      );
      setSelectedLeads(filteredLeads);

      if (filteredLeads.length > 0) {
        saveToExistingCampaignMutation.mutate(selectedExistingCampaign);
      } else {
        toast({ title: "No new leads to save after removing duplicates" });
      }
    } else {
      // For merge/overwrite, save all (backend handles the logic)
      saveToExistingCampaignMutation.mutate(selectedExistingCampaign);
    }
  };

  const handleSubmit = () => {
    if (!name.trim()) {
      toast({ title: "Campaign name is required", variant: "destructive" });
      return;
    }
    if (!agentId) {
      toast({ title: "Please select an agent", variant: "destructive" });
      return;
    }
    if (selectedLeads.length === 0) {
      toast({ title: "Please add at least one lead", variant: "destructive" });
      return;
    }

    setIsSubmitting(true);
    createCampaignMutation.mutate();
  };

  // Validation checks for campaign creation
  const isSettingsValid = name.trim() && agentId;
  const hasLeads = selectedLeads.length > 0;

  return (
    <div className={`${embedded ? "min-h-[calc(100vh-17rem)] rounded-xl border overflow-hidden" : "min-h-screen"} bg-gradient-to-br from-purple-50 via-white to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-purple-950 flex`}>
      {/* ChatGPT-style Sidebar - Session History */}
      <div className={`${showSessionHistory ? 'w-72' : 'w-0'} flex-shrink-0 transition-all duration-300 overflow-hidden`}>
        <div className={`${embedded ? "h-auto min-h-[calc(100vh-17rem)]" : "h-screen"} w-72 bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800 flex flex-col`}>
          {/* Sidebar Header */}
          <div className="p-3 border-b border-gray-200 dark:border-gray-800">
            <Button
              onClick={createNewSession}
              className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white justify-start gap-3"
            >
              <Plus className="h-4 w-4" />
              New Search
            </Button>
          </div>

          {/* Sessions List */}
          <ScrollArea className="flex-1 px-2 py-3">
            <div className="space-y-1">
              {sessions.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-40" />
                  <p className="text-xs">No saved sessions</p>
                </div>
              ) : (
                sessions.map((session) => {
                  const isActive = session.id === currentSessionId;
                  const totalResults = session.allSearchResults?.length || 0;
                  return (
                    <div
                      key={session.id}
                      onClick={() => loadSession(session.id)}
                      className={`group relative p-2.5 rounded-lg cursor-pointer transition-colors ${
                        isActive
                          ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-900 dark:text-purple-100'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800/50'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <MessageSquare className={`h-4 w-4 flex-shrink-0 ${isActive ? 'text-purple-600 dark:text-purple-400' : 'opacity-60'}`} />
                        <p className="text-sm truncate flex-1">{session.title}</p>
                      </div>
                      <div className="flex items-center gap-2 mt-1 text-xs text-gray-500 dark:text-gray-400">
                        <span>{formatDate(session.updatedAt, 'MMM d, yyyy')}</span>
                        {totalResults > 0 && (
                          <>
                            <span>•</span>
                            <span>{totalResults} leads</span>
                          </>
                        )}
                      </div>
                      {/* Actions on hover */}
                      <div className="absolute right-2 top-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white">
                              <MoreVertical className="h-3.5 w-3.5" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-40">
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); downloadSessionCSV(session, e); }}>
                              <Download className="h-4 w-4 mr-2" />
                              Download CSV
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={(e) => { e.stopPropagation(); deleteSession(session.id, e as any); }}
                              className="text-red-500"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </ScrollArea>

          {/* Sidebar Footer */}
          <div className="p-3 border-t border-gray-200 dark:border-gray-800">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setLocation("/app/campaigns")}
              className="w-full justify-start gap-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-800"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Campaigns
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Bar */}
        <div className="h-14 border-b bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm flex items-center justify-between px-4 flex-shrink-0">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowSessionHistory(!showSessionHistory)}
              className="lg:hidden"
            >
              <History className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowSessionHistory(!showSessionHistory)}
              className="hidden lg:flex"
            >
              {showSessionHistory ? <X className="h-4 w-4" /> : <History className="h-4 w-4" />}
            </Button>
            {!embedded && (
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
                  <Sparkles className="h-4 w-4 text-white" />
                </div>
                <div>
                  <h1 className="text-base font-semibold">AI B2B Lead</h1>
                </div>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {chatMessages.length > 1 && (
              <Button variant="ghost" size="sm" onClick={clearSession} className="gap-2 text-sm">
                <Plus className="h-4 w-4" />
                New Chat
              </Button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-6">
            <TabsTrigger value="search-results" className="flex items-center gap-2">
              <Search className="h-4 w-4" />
              Search & Results
            </TabsTrigger>
            <TabsTrigger value="campaign-settings" className="flex items-center gap-2">
              <Target className="h-4 w-4" />
              Campaign Settings
            </TabsTrigger>
            <TabsTrigger value="review" className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4" />
              Review
              {selectedLeads.length > 0 && (
                <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-xs">
                  {selectedLeads.length}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Tab 1: Search & Results */}
              <TabsContent value="search-results" className="mt-0 space-y-6">
              <>
              {/* Full Height Chat Interface */}
              <div className={`flex flex-col ${embedded ? "h-[calc(100vh-24rem)] min-h-[18rem]" : "h-[calc(100vh-140px)]"} bg-white dark:bg-gray-950 rounded-lg border`} data-testid="search-chat-card">
                {/* Tiny Header */}
                <div className="flex items-center justify-between px-3 py-1.5 border-b">
                  <span className="text-xs font-medium text-muted-foreground">AI B2B Lead</span>
                  {chatMessages.length > 1 && (
                    <Button variant="ghost" size="sm" onClick={clearSession} className="h-6 text-xs px-2">
                      Clear
                    </Button>
                  )}
                </div>

                {/* Chat Messages Area - Maximum Space */}
                <ScrollArea className="flex-1">
                  <div className="px-4 py-3 space-y-3">
                    {/* All Messages Including Welcome */}
                    {chatMessages.map((message, index) => (
                      <div key={message.id} className="flex gap-3">
                        {/* Avatar */}
                        <div className={`h-7 w-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                          message.role === 'assistant'
                            ? 'bg-purple-600'
                            : 'bg-gray-300 dark:bg-gray-600'
                        }`}>
                          {message.role === 'assistant' ? (
                            <Bot className="h-4 w-4 text-white" />
                          ) : (
                            <User className="h-4 w-4 text-gray-600 dark:text-gray-300" />
                          )}
                        </div>

                        {/* Message Content */}
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-muted-foreground mb-1">
                            {message.role === 'assistant' ? 'AI B2B Lead' : 'You'}
                          </p>
                          <div className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">
                            {message.content}
                          </div>

                          {/* Search Params as compact tags */}
                          {message.searchParams && Object.keys(message.searchParams).length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mt-2">
                              {message.searchParams.person_titles?.map((title, i) => (
                                <span key={`title-${i}`} className="px-2 py-0.5 rounded text-xs bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
                                  {title}
                                </span>
                              ))}
                              {message.searchParams.person_seniorities?.map((s, i) => (
                                <span key={`sen-${i}`} className="px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                                  {s}
                                </span>
                              ))}
                              {message.searchParams.person_locations?.map((loc, i) => (
                                <span key={`loc-${i}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">
                                  <MapPin className="h-3 w-3" />{loc}
                                </span>
                              ))}
                              {message.searchParams.organization_industries?.map((ind, i) => (
                                <span key={`ind-${i}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300">
                                  <Building className="h-3 w-3" />{ind}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}

                    {/* Loading State */}
                    {isSearching && (
                      <div className="flex gap-3">
                        <div className="h-7 w-7 rounded-full bg-purple-600 flex items-center justify-center flex-shrink-0">
                          <Bot className="h-4 w-4 text-white" />
                        </div>
                        <div className="flex-1">
                          <p className="text-xs font-medium text-muted-foreground mb-1">AI B2B Lead</p>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            <span>Searching for leads...</span>
                          </div>
                        </div>
                      </div>
                    )}
                    <div ref={chatEndRef} />
                  </div>
                </ScrollArea>

                {/* Minimal Input Area */}
                <div className="border-t p-2 flex gap-2 items-center">
                  <Textarea
                    ref={inputRef}
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Search for leads... (e.g., 'CEOs at tech startups')"
                    rows={1}
                    className="flex-1 resize-none min-h-[36px] max-h-[80px] py-2 px-3 text-sm rounded-md border"
                    disabled={isSearching}
                  />
                  <Button
                    onClick={() => handleSendMessage()}
                    disabled={isSearching || !chatInput.trim()}
                    size="icon"
                    className="h-9 w-9 rounded-md bg-purple-600 hover:bg-purple-700 flex-shrink-0"
                  >
                    {isSearching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  </Button>
                </div>
              </div>

              {/* Loading Card - Shows while searching and enriching */}
              {isEnrichingPhones && allSearchResults.length === 0 && (
                <Card className="mt-4" data-testid="enrichment-loading-card">
                  <CardContent className="py-12">
                    <div className="flex flex-col items-center justify-center text-center space-y-4">
                      <div className="relative">
                        <Loader2 className="h-12 w-12 animate-spin text-purple-500" />
                        <div className="absolute inset-0 h-12 w-12 rounded-full border-4 border-purple-100 animate-pulse" />
                      </div>
                      <div className="space-y-2">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                          Searching & Enriching Leads
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 max-w-md">
                          Finding contacts and retrieving phone numbers...
                          <br />
                          This may take 10-15 seconds for best results.
                        </p>
                      </div>
                      <Badge variant="outline" className="text-blue-600 border-blue-300 bg-blue-50 animate-pulse">
                        <Phone className="h-3 w-3 mr-1" />
                        Enriching phone numbers...
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Results Table */}
              {showResultsTable && allSearchResults.length > 0 && (
                <Card className="mt-4" data-testid="results-table-card">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <TableIcon className="h-5 w-5 text-purple-500" />
                        Search Results
                        <Badge variant="secondary">{allSearchResults.length}</Badge>
                      </CardTitle>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={selectAllResults}
                          disabled={allSearchResults.filter(l => !isLeadSaved(l)).length === 0}
                        >
                          Select All ({allSearchResults.filter(l => !isLeadSaved(l)).length})
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={deselectAllResults}
                          disabled={selectedResultIds.size === 0}
                        >
                          Deselect All
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={downloadSearchResultsCSV}
                        >
                          <Download className="h-4 w-4 mr-1" />
                          CSV
                        </Button>
                        <Button
                          size="sm"
                          onClick={saveSelectedResults}
                          disabled={selectedResultIds.size === 0}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <Save className="h-4 w-4 mr-1" />
                          Save Selected ({allSearchResults.filter(l => selectedResultIds.has(l.id || '') && l.phone && !isLeadSaved(l)).length}/{selectedResultIds.size})
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowResultsTable(false)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <CardDescription className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span>{allSearchResults.filter(l => l.phone).length} of {allSearchResults.length} leads with phone numbers.</span>
                        {isEnrichingPhones && (
                          <Badge variant="outline" className="text-blue-600 border-blue-300 bg-blue-50 animate-pulse">
                            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                            Enriching phones...
                          </Badge>
                        )}
                        {!isEnrichingPhones && allSearchResults.filter(l => l.phone).length === 0 && allSearchResults.length > 0 && (
                          <Badge variant="outline" className="text-amber-600 border-amber-300 bg-amber-50">
                            <AlertCircle className="h-3 w-3 mr-1" />
                            No phones found
                          </Badge>
                        )}
                      </div>
                      <div className="text-xs">
                        {isEnrichingPhones
                          ? "Retrieving phone numbers... This may take a few seconds."
                          : allSearchResults.filter(l => l.phone).length === 0 && allSearchResults.length > 0
                          ? "No phone numbers available for these contacts."
                          : "Select leads with phone numbers and click 'Save' to add them to your campaign."}
                      </div>
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="border rounded-lg overflow-x-auto">
                      <div className="h-[min(400px,55vh)] overflow-y-auto">
                        <Table className="min-w-[1400px] w-full">
                          <TableHeader className="sticky top-0 bg-gray-50 dark:bg-gray-800 z-10">
                            <TableRow>
                              <TableHead className="w-10 sticky left-0 bg-gray-50 dark:bg-gray-800 z-20">
                                <Checkbox
                                  checked={selectedResultIds.size > 0 && selectedResultIds.size === allSearchResults.filter(l => !isLeadSaved(l)).length}
                                  onCheckedChange={(checked) => {
                                    if (checked) selectAllResults();
                                    else deselectAllResults();
                                  }}
                                />
                              </TableHead>
                              <TableHead className="min-w-[150px]">Name</TableHead>
                              <TableHead className="min-w-[140px]">Title</TableHead>
                              <TableHead className="min-w-[140px]">Company</TableHead>
                              <TableHead className="min-w-[100px]">Industry</TableHead>
                              <TableHead className="min-w-[120px]">Location</TableHead>
                              <TableHead className="min-w-[130px]">Phone</TableHead>
                              <TableHead className="min-w-[150px]">Email</TableHead>
                              <TableHead className="min-w-[80px]">Employees</TableHead>
                              <TableHead className="min-w-[80px]">LinkedIn</TableHead>
                              <TableHead className="min-w-[80px]">Website</TableHead>
                              <TableHead className="w-24 text-right sticky right-0 bg-gray-50 dark:bg-gray-800 z-20">Action</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {allSearchResults
                              .slice((resultsPage - 1) * resultsPerPage, resultsPage * resultsPerPage)
                              .map((lead) => {
                              const isSaved = isLeadSaved(lead);
                              const hasPhone = !!lead.phone;
                              const isSelected = selectedResultIds.has(lead.id || '');

                              return (
                                <TableRow
                                  key={lead.id}
                                  className={`cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors ${isSaved ? 'bg-green-50 dark:bg-green-950/20' : ''} ${!hasPhone ? 'bg-amber-50/50 dark:bg-amber-950/10' : ''} ${isSelected ? 'bg-purple-50 dark:bg-purple-950/20' : ''}`}
                                  onClick={() => !isSaved && lead.id && toggleResultSelection(lead.id)}
                                >
                                  <TableCell className={`sticky left-0 z-10 ${isSaved ? 'bg-green-50 dark:bg-green-950/20' : isSelected ? 'bg-purple-50 dark:bg-purple-950/20' : !hasPhone ? 'bg-amber-50/50 dark:bg-amber-950/10' : 'bg-white dark:bg-gray-900'}`}>
                                    <Checkbox
                                      checked={isSelected}
                                      onCheckedChange={() => {
                                        if (!isSaved && lead.id) {
                                          toggleResultSelection(lead.id);
                                        }
                                      }}
                                      disabled={isSaved}
                                      onClick={(e) => e.stopPropagation()}
                                    />
                                  </TableCell>
                                  <TableCell className="font-medium">
                                    <div className="flex flex-col">
                                      <span>{lead.firstName} {lead.lastName}</span>
                                      {lead.seniority && (
                                        <Badge variant="outline" className="text-xs w-fit mt-1">
                                          {lead.seniority}
                                        </Badge>
                                      )}
                                    </div>
                                  </TableCell>
                                  <TableCell className="text-sm text-muted-foreground">
                                    {lead.title || '-'}
                                  </TableCell>
                                  <TableCell className="text-sm">
                                    <div className="flex items-center gap-1">
                                      <Building className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                                      <span className="truncate max-w-[120px]">{lead.company || '-'}</span>
                                    </div>
                                  </TableCell>
                                  <TableCell className="text-sm">
                                    {lead.industry ? (
                                      <Badge variant="secondary" className="text-xs">
                                        {lead.industry}
                                      </Badge>
                                    ) : (
                                      <span className="text-muted-foreground">-</span>
                                    )}
                                  </TableCell>
                                  <TableCell className="text-sm">
                                    {(lead.city || lead.state || lead.country) ? (
                                      <div className="flex items-center gap-1">
                                        <MapPin className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                                        <span className="truncate max-w-[100px]">
                                          {[lead.city, lead.state, lead.country].filter(Boolean).join(', ')}
                                        </span>
                                      </div>
                                    ) : (
                                      <span className="text-muted-foreground">-</span>
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    {hasPhone ? (
                                      <div className="flex items-center gap-1 text-green-600">
                                        <Phone className="h-3 w-3" />
                                        <span className="text-sm">{lead.phone}</span>
                                      </div>
                                    ) : (
                                      <span className="text-sm text-muted-foreground">No phone</span>
                                    )}
                                  </TableCell>
                                  <TableCell className="text-sm">
                                    {lead.email ? (
                                      <div className="flex items-center gap-1">
                                        <Mail className="h-3 w-3 text-muted-foreground" />
                                        <span className="truncate max-w-[130px]">{lead.email}</span>
                                      </div>
                                    ) : (
                                      '-'
                                    )}
                                  </TableCell>
                                  <TableCell className="text-sm text-center">
                                    {lead.employeeCount ? (
                                      <Badge variant="outline" className="text-xs">
                                        {lead.employeeCount.toLocaleString()}
                                      </Badge>
                                    ) : (
                                      <span className="text-muted-foreground">-</span>
                                    )}
                                  </TableCell>
                                  <TableCell className="text-sm">
                                    {lead.linkedinUrl ? (
                                      <a
                                        href={lead.linkedinUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-1 text-blue-600 hover:text-blue-800"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <Linkedin className="h-4 w-4" />
                                      </a>
                                    ) : (
                                      <span className="text-muted-foreground">-</span>
                                    )}
                                  </TableCell>
                                  <TableCell className="text-sm">
                                    {lead.website ? (
                                      <a
                                        href={lead.website.startsWith('http') ? lead.website : `https://${lead.website}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-1 text-blue-600 hover:text-blue-800"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <ExternalLink className="h-4 w-4" />
                                      </a>
                                    ) : (
                                      <span className="text-muted-foreground">-</span>
                                    )}
                                  </TableCell>
                                  <TableCell className={`text-right sticky right-0 z-10 ${isSaved ? 'bg-green-50 dark:bg-green-950/20' : isSelected ? 'bg-purple-50 dark:bg-purple-950/20' : !hasPhone ? 'bg-amber-50/50 dark:bg-amber-950/10' : 'bg-white dark:bg-gray-900'}`}>
                                    {isSaved ? (
                                      <Badge variant="secondary" className="bg-green-100 text-green-700">
                                        <Check className="h-3 w-3 mr-1" />
                                        Saved
                                      </Badge>
                                    ) : hasPhone ? (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          saveLead(lead);
                                        }}
                                        className="h-7 text-xs"
                                      >
                                        <Plus className="h-3 w-3" />
                                      </Button>
                                    ) : (
                                      <span className="text-xs text-muted-foreground">-</span>
                                    )}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </div>
                    </div>

                    {/* Pagination */}
                    {allSearchResults.length > resultsPerPage && (
                      <div className="flex items-center justify-between mt-4 pt-4 border-t">
                        <div className="text-sm text-muted-foreground">
                          Showing {((resultsPage - 1) * resultsPerPage) + 1} - {Math.min(resultsPage * resultsPerPage, allSearchResults.length)} of {allSearchResults.length}
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setResultsPage(p => Math.max(1, p - 1))}
                            disabled={resultsPage === 1}
                          >
                            <ArrowLeft className="h-4 w-4" />
                          </Button>
                          <span className="text-sm">
                            Page {resultsPage} of {Math.ceil(allSearchResults.length / resultsPerPage)}
                          </span>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setResultsPage(p => Math.min(Math.ceil(allSearchResults.length / resultsPerPage), p + 1))}
                            disabled={resultsPage >= Math.ceil(allSearchResults.length / resultsPerPage)}
                          >
                            <ArrowRight className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Quick Actions */}
                    <div className="flex items-center justify-between mt-4 pt-4 border-t">
                      <div className="text-sm text-muted-foreground flex items-center gap-2">
                        {isEnrichingPhones ? (
                          <>
                            <Loader2 className="h-3 w-3 animate-spin" />
                            Enriching phones...
                          </>
                        ) : (
                          <>{allSearchResults.filter(l => l.phone).length} leads with phone</>
                        )}
                        {' '}• {selectedLeads.length} saved to campaign
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          onClick={saveAllLeadsWithPhone}
                          disabled={allSearchResults.filter(l => l.phone && !isLeadSaved(l)).length === 0}
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Save All With Phone ({allSearchResults.filter(l => l.phone && !isLeadSaved(l)).length})
                        </Button>
                        {selectedLeads.length > 0 && (
                          <Button
                            onClick={() => setActiveTab("campaign-settings")}
                            className="bg-purple-600 hover:bg-purple-700"
                          >
                            <ArrowRight className="h-4 w-4 mr-1" />
                            Configure Campaign
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
              </>
              </TabsContent>

              {/* Tab 2: Campaign Settings */}
              <TabsContent value="campaign-settings" className="mt-0">
              <Card data-testid="campaign-settings-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="h-5 w-5 text-purple-500" />
                    Campaign Details
                  </CardTitle>
                  <CardDescription>Configure your campaign settings</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Campaign Name *</Label>
                      <Input
                        id="name"
                        data-testid="campaign-name-input"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Q4 Lead Generation"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Campaign Type *</Label>
                      <Select value={type} onValueChange={setType}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {CAMPAIGN_TYPES.map((t) => (
                            <SelectItem key={t} value={t}>{t}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>AI Agent *</Label>
                    <Select value={agentId} onValueChange={setAgentId}>
                      <SelectTrigger data-testid="agent-select">
                        <SelectValue placeholder="Select an agent" />
                      </SelectTrigger>
                      <SelectContent>
                        {agents.map((agent) => (
                          <SelectItem key={agent.id} value={agent.id}>
                            {agent.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="goal">Campaign Goal (Optional)</Label>
                    <Textarea
                      id="goal"
                      value={goal}
                      onChange={(e) => setGoal(e.target.value)}
                      placeholder="Describe what you want to achieve..."
                      rows={3}
                    />
                  </div>

                  {/* Timezone Section - Always visible for follow-up scheduling */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Globe className="h-4 w-4" />
                      Campaign Timezone <span className="text-red-500">*</span>
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Used for scheduling calls and follow-ups in the contact's local time
                    </p>
                    <Select value={scheduleTimezone} onValueChange={setScheduleTimezone}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select timezone" />
                      </SelectTrigger>
                      <SelectContent>
                        {flatTimezoneList.map((tz) => (
                          <SelectItem key={tz.value} value={tz.value}>
                            {tz.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Schedule Section */}
                  <div className="border rounded-lg p-4 space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-muted-foreground" />
                        <Label>Schedule Calls</Label>
                      </div>
                      <Switch checked={scheduleEnabled} onCheckedChange={setScheduleEnabled} />
                    </div>

                    {scheduleEnabled && (
                      <div className="space-y-4 pt-2">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Start Time</Label>
                            <Input
                              type="time"
                              value={scheduleTimeStart}
                              onChange={(e) => setScheduleTimeStart(e.target.value)}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>End Time</Label>
                            <Input
                              type="time"
                              value={scheduleTimeEnd}
                              onChange={(e) => setScheduleTimeEnd(e.target.value)}
                            />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label>Active Days</Label>
                          <div className="flex gap-2">
                            {WEEKDAYS.map((day) => (
                              <Button
                                key={day.id}
                                type="button"
                                variant={scheduleDays.includes(day.id) ? "default" : "outline"}
                                size="sm"
                                onClick={() => toggleDay(day.id)}
                              >
                                {day.label}
                              </Button>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Navigation Buttons */}
                  <div className="flex items-center justify-between pt-4 border-t">
                    <Button
                      variant="outline"
                      onClick={() => setActiveTab("search-results")}
                    >
                      <ArrowLeft className="h-4 w-4 mr-2" />
                      Back to Search
                    </Button>
                    <Button
                      onClick={() => setActiveTab("review")}
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      Next: Review
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
              </TabsContent>

              {/* Tab 3: Review */}
              <TabsContent value="review" className="mt-0">
              <Card data-testid="review-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 text-green-500" />
                    Review & Create
                  </CardTitle>
                  <CardDescription>Review your campaign before creating</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Validation warnings */}
                  {(!name.trim() || !agentId || !scheduleTimezone) && (
                    <div className="p-4 bg-amber-50 dark:bg-amber-950/30 rounded-lg border border-amber-200">
                      <div className="flex items-center gap-2 font-medium text-amber-800 dark:text-amber-200">
                        <AlertCircle className="h-4 w-4" />
                        Missing Required Fields
                      </div>
                      <ul className="text-sm text-amber-700 dark:text-amber-300 mt-2 list-disc list-inside">
                        {!name.trim() && <li>Campaign name is required</li>}
                        {!agentId && <li>Please select an AI agent</li>}
                        {!scheduleTimezone && <li>Timezone is required for follow-up scheduling</li>}
                      </ul>
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-3"
                        onClick={() => setActiveTab("campaign-settings")}
                      >
                        Go to Campaign Settings
                      </Button>
                    </div>
                  )}

                  {selectedLeads.length === 0 && (
                    <div className="p-4 bg-amber-50 dark:bg-amber-950/30 rounded-lg border border-amber-200">
                      <div className="flex items-center gap-2 font-medium text-amber-800 dark:text-amber-200">
                        <Users className="h-4 w-4" />
                        No Leads Selected
                      </div>
                      <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                        Search and select leads to add to your campaign.
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-3"
                        onClick={() => setActiveTab("search-results")}
                      >
                        Go to Search & Results
                      </Button>
                    </div>
                  )}

                  {/* Campaign Summary */}
                  {name.trim() && agentId && (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-muted-foreground">Campaign Name</Label>
                          <p className="font-medium">{name}</p>
                        </div>
                        <div>
                          <Label className="text-muted-foreground">Type</Label>
                          <p className="font-medium">{type}</p>
                        </div>
                      </div>

                      <div>
                        <Label className="text-muted-foreground">Agent</Label>
                        <p className="font-medium">
                          {agents.find(a => a.id === agentId)?.name || 'Unknown'}
                        </p>
                      </div>

                      {goal && (
                        <div>
                          <Label className="text-muted-foreground">Goal</Label>
                          <p className="text-sm">{goal}</p>
                        </div>
                      )}

                      {scheduleEnabled && (
                        <div className="p-4 bg-blue-50 dark:bg-blue-950/30 rounded-lg">
                          <div className="flex items-center gap-2 font-medium text-blue-800 dark:text-blue-200">
                            <Calendar className="h-4 w-4" />
                            Scheduled Calls
                          </div>
                          <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                            {scheduleTimeStart} - {scheduleTimeEnd} ({scheduleTimezone})
                          </p>
                          <p className="text-sm text-blue-700 dark:text-blue-300">
                            Days: {scheduleDays.map(d => d.charAt(0).toUpperCase() + d.slice(1, 3)).join(', ')}
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Leads Summary */}
                  {selectedLeads.length > 0 && (
                    <div className="p-4 bg-green-50 dark:bg-green-950/30 rounded-lg">
                      <div className="flex items-center gap-2 font-medium text-green-800 dark:text-green-200">
                        <Users className="h-4 w-4" />
                        {selectedLeads.length} Leads Ready
                      </div>
                      <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                        All leads have valid phone numbers and are ready for calling
                      </p>
                    </div>
                  )}

                  {/* Create Campaign Button */}
                  <div className="pt-4 border-t">
                    <Button
                      onClick={handleSubmit}
                      disabled={isSubmitting || !name.trim() || !agentId || !scheduleTimezone || selectedLeads.length === 0}
                      className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
                      size="lg"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Creating Campaign...
                        </>
                      ) : (
                        <>
                          <CheckCircle2 className="h-4 w-4 mr-2" />
                          Create Campaign with {selectedLeads.length} Leads
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
              </TabsContent>
            </div>



          {/* Sidebar with Selected Leads */}
          <div className="space-y-6">
            <Card className="sticky top-24" data-testid="selected-leads-panel">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Users className="h-5 w-5 text-purple-500" />
                    Selected Leads
                  </CardTitle>
                  <Badge variant={selectedLeads.length > 0 ? "default" : "secondary"} data-testid="leads-count-badge">
                    {selectedLeads.length}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {selectedLeads.length > 0 ? (
                  <>
                    <div className="flex justify-end">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={clearAllLeads}
                        className="text-destructive text-xs"
                      >
                        <Trash2 className="h-3 w-3 mr-1" />
                        Clear All
                      </Button>
                    </div>
                    <ScrollArea className="h-[400px]">
                      <div className="space-y-2 pr-4">
                        {selectedLeads.map((lead, index) => (
                          <div
                            key={index}
                            className="flex items-start justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded-lg text-xs"
                          >
                            <div className="flex-1 min-w-0">
                              <div className="font-medium truncate">
                                {lead.firstName} {lead.lastName}
                              </div>
                              {lead.title && (
                                <div className="text-muted-foreground truncate">{lead.title}</div>
                              )}
                              {lead.company && (
                                <div className="text-muted-foreground truncate flex items-center gap-1">
                                  <Building className="h-3 w-3" />
                                  {lead.company}
                                </div>
                              )}
                              <div className="text-green-600 flex items-center gap-1 mt-1">
                                <Phone className="h-3 w-3" />
                                {lead.phone}
                              </div>
                              {lead.linkedinUrl && (
                                <a
                                  href={lead.linkedinUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 flex items-center gap-1 mt-1 hover:underline"
                                >
                                  <Linkedin className="h-3 w-3" />
                                  <span className="text-xs">LinkedIn</span>
                                </a>
                              )}
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeLead(index)}
                              className="h-6 w-6 p-0"
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>

                    {/* Lead Stats */}
                    <div className="pt-3 border-t space-y-2">
                      <div className="text-xs text-muted-foreground">Lead Statistics</div>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div className="p-2 bg-gray-50 dark:bg-gray-800 rounded">
                          <div className="font-medium">{selectedLeads.filter(l => l.email).length}</div>
                          <div className="text-muted-foreground">With Email</div>
                        </div>
                        <div className="p-2 bg-gray-50 dark:bg-gray-800 rounded">
                          <div className="font-medium">{selectedLeads.filter(l => l.company).length}</div>
                          <div className="text-muted-foreground">With Company</div>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Users className="h-10 w-10 mx-auto mb-3 opacity-20" />
                    <p className="text-sm">No leads selected</p>
                    <p className="text-xs mt-1">
                      {activeTab === "search-results"
                        ? "Use the chat to search for leads"
                        : "Go to Search & Results to find leads"}
                    </p>
                  </div>
                )}

                {/* Campaign Summary */}
                <div className="pt-3 border-t space-y-2">
                  <div className="text-xs font-medium">Campaign</div>
                  <div className="space-y-1 text-xs">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Name</span>
                      <span className="font-medium truncate ml-2">{name || "-"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Type</span>
                      <span className="font-medium">{type}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Agent</span>
                      <span className="font-medium truncate ml-2">
                        {agents.find(a => a.id === agentId)?.name || "-"}
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          </div>
        </Tabs>
        </div>

      {/* Campaign Selection Modal */}
      <Dialog open={showCampaignModal} onOpenChange={setShowCampaignModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Save className="h-5 w-5 text-purple-500" />
              Save to Campaign
            </DialogTitle>
            <DialogDescription>
              Save {selectedLeads.length} selected leads to an existing campaign or create a new one.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <RadioGroup
              value={createNewCampaign ? "new" : "existing"}
              onValueChange={(v) => setCreateNewCampaign(v === "new")}
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="new" id="new" />
                <Label htmlFor="new" className="cursor-pointer">Create New Campaign</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="existing" id="existing" />
                <Label htmlFor="existing" className="cursor-pointer">Add to Existing Campaign</Label>
              </div>
            </RadioGroup>

            {!createNewCampaign && (
              <div className="space-y-2">
                <Label>Select Campaign</Label>
                <Select value={selectedExistingCampaign} onValueChange={setSelectedExistingCampaign}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a campaign..." />
                  </SelectTrigger>
                  <SelectContent>
                    {existingCampaigns.map((campaign) => (
                      <SelectItem key={campaign.id} value={campaign.id}>
                        <div className="flex items-center justify-between w-full">
                          <span>{campaign.name}</span>
                          <Badge variant="secondary" className="ml-2 text-xs">
                            {campaign.contactCount || 0} leads
                          </Badge>
                        </div>
                      </SelectItem>
                    ))}
                    {existingCampaigns.length === 0 && (
                      <div className="text-sm text-muted-foreground p-2">
                        No existing campaigns found
                      </div>
                    )}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Lead Summary */}
            <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div className="text-sm font-medium mb-2">Lead Summary</div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Total Leads:</span>
                  <span className="font-medium">{selectedLeads.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">With Phone:</span>
                  <span className="font-medium text-green-600">{selectedLeads.filter(l => l.phone).length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">With Email:</span>
                  <span className="font-medium">{selectedLeads.filter(l => l.email).length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">With Company:</span>
                  <span className="font-medium">{selectedLeads.filter(l => l.company).length}</span>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCampaignModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveToCampaign}
              disabled={isSavingToCampaign || (!createNewCampaign && !selectedExistingCampaign)}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {isSavingToCampaign ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : createNewCampaign ? (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Create New Campaign
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save to Campaign
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duplicate Detection Modal */}
      <Dialog open={showDuplicateModal} onOpenChange={setShowDuplicateModal}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertCircle className="h-5 w-5" />
              Duplicate Leads Found
            </DialogTitle>
            <DialogDescription>
              {duplicateLeads.length} lead(s) already exist in this campaign. Choose how to handle them:
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <RadioGroup value={duplicateAction} onValueChange={(v: 'skip' | 'merge' | 'overwrite') => setDuplicateAction(v)}>
              <div className="flex items-start space-x-2 p-3 rounded-lg border hover:bg-gray-50 dark:hover:bg-gray-800">
                <RadioGroupItem value="skip" id="skip" className="mt-1" />
                <div>
                  <Label htmlFor="skip" className="cursor-pointer font-medium">Skip Duplicates</Label>
                  <p className="text-xs text-muted-foreground">
                    Only import new leads, skip existing ones
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-2 p-3 rounded-lg border hover:bg-gray-50 dark:hover:bg-gray-800">
                <RadioGroupItem value="merge" id="merge" className="mt-1" />
                <div>
                  <Label htmlFor="merge" className="cursor-pointer font-medium">Merge Data</Label>
                  <p className="text-xs text-muted-foreground">
                    Update existing leads with new data, keep existing fields if not overwritten
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-2 p-3 rounded-lg border hover:bg-gray-50 dark:hover:bg-gray-800">
                <RadioGroupItem value="overwrite" id="overwrite" className="mt-1" />
                <div>
                  <Label htmlFor="overwrite" className="cursor-pointer font-medium">Overwrite</Label>
                  <p className="text-xs text-muted-foreground">
                    Replace existing lead data with newly found data
                  </p>
                </div>
              </div>
            </RadioGroup>

            {/* Duplicate list preview */}
            <div className="max-h-[150px] overflow-y-auto">
              <div className="text-xs font-medium mb-2">Duplicates Found:</div>
              {duplicateLeads.slice(0, 5).map((lead, i) => (
                <div key={i} className="text-xs text-muted-foreground py-1 border-b">
                  {lead.firstName} {lead.lastName} - {lead.phone || lead.email}
                </div>
              ))}
              {duplicateLeads.length > 5 && (
                <div className="text-xs text-muted-foreground py-1">
                  ... and {duplicateLeads.length - 5} more
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDuplicateModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleDuplicateResolution} className="bg-amber-600 hover:bg-amber-700">
              Continue with {duplicateAction === 'skip' ? 'Skip' : duplicateAction === 'merge' ? 'Merge' : 'Overwrite'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
}

// No default export on purpose: AI B2B Lead (pages/AiB2bLeadPage.tsx) owns the route and renders
// ApolloProspectSearch as its Apollo.io tab.
